import { ButtonImage, ButtonRoot } from '@/components/common/Button'
import { CardLink } from '@/components/common/Card'
import { FloatingTooltip } from '@/components/common/Floating'
import { Icon } from '@/components/icons/Icon'
import { type CardMapType } from '@/defs/card'
import { COLOR_ID_LABELS, type ColorIdType } from '@/defs/mtg'
import { type RecType } from '@/defs/rec'
import { clipboardActions } from '@/store/clipboard'
import { useReduxDispatch } from '@/store/hooks'
import { isInColorId } from '@/utils/cardUtil'
import { cn } from '@/utils/commonUtil'

import styles from './ClipboardCard.module.scss'

type Props = {
  cardMap: CardMapType
  commanderColorId?: ColorIdType
  rec: RecType
  variant: 'clipboard' | 'pending'
}

export const ClipboardCard = ({ cardMap, commanderColorId, rec, variant }: Props) => {
  const dispatch = useReduxDispatch()
  const { cardId } = rec
  const card = cardMap[cardId]

  const onPromoteClick = () => {
    dispatch(clipboardActions.promote({ card: rec, cardMap }))
  }

  const onDemoteClick = () => {
    dispatch(clipboardActions.demote(rec))
  }

  const onAddClick = () => {
    dispatch(clipboardActions.add([rec]))
  }

  const onRemoveClick = () => {
    dispatch(clipboardActions.remove([rec]))
  }

  const checkInColorId = (colorId?: ColorIdType) =>
    !commanderColorId || (!!colorId && isInColorId(colorId, commanderColorId))

  return (
    <ButtonImage as='div' borderNone colorId={card?.colorId} image={card?.imageArt} padding='none' size='sm'>
      <div className={styles.container}>
        <div className={styles.content}>
          <CardLink className={styles.name} rec={rec}>
            <span>{card?.name || 'Loading...'}</span>
          </CardLink>
          <div className={styles.actions}>
            {variant === 'clipboard' && (
              <>
                {commanderColorId && !checkInColorId(card?.colorId) && (
                  <FloatingTooltip
                    content={
                      <p>
                        <strong>{card?.name || 'This card'}</strong>{' '}
                        {card?.colorId && (
                          <span>
                            (<Icon name={COLOR_ID_LABELS[card.colorId].icon} />)
                          </span>
                        )}{' '}
                        <span>cannot be included in the chosen commander&apos;s color identity</span>{' '}
                        <span>
                          (
                          <Icon name={COLOR_ID_LABELS[commanderColorId].icon} />
                          ).
                        </span>
                      </p>
                    }
                  >
                    <ButtonRoot aria-label='Invalid' className={cn(styles.button, styles.colorIdWarning)}>
                      <Icon name='circleExclamation' />
                    </ButtonRoot>
                  </FloatingTooltip>
                )}
                {(card?.isLegalCommander || card?.isBackground) &&
                  (rec.role === 'commander' ? (
                    <ButtonRoot aria-label='Demote as Commander' className={styles.button} onClick={onDemoteClick}>
                      <Icon name='arrowDown' />
                    </ButtonRoot>
                  ) : (
                    <ButtonRoot aria-label='Promote to Commander' className={styles.button} onClick={onPromoteClick}>
                      <Icon name='crown' />
                    </ButtonRoot>
                  ))}
              </>
            )}
            {variant === 'pending' && (
              <ButtonRoot aria-label='Add to Clipboard' className={styles.button} onClick={onAddClick}>
                <Icon name='plus' />
              </ButtonRoot>
            )}
            <ButtonRoot
              aria-label='Remove from Clipboard'
              className={cn(styles.button, styles.remove)}
              onClick={onRemoveClick}
            >
              <Icon name='xMark' />
            </ButtonRoot>
          </div>
        </div>
      </div>
    </ButtonImage>
  )
}
